import React, { FC } from 'react';
import PostComposerContentText from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentText';
import { PostComposerContentProps } from 'app/modules/posts/modules/post/components/composer/PostComposerContent';
import PostComposerContentMediaAttachments from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentMediaAttachemnts';
import { PseudoPostType } from 'constants/post';
import PostsConfig from 'app/config/posts';
import { Checkbox, Field, Select, TextInput } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import YoutubeFields from 'app/modules/posts/modules/post/constants/youtubeFields';

type Props = PostComposerContentProps;

const PostComposerContentYoutube: FC<Props> = ({
  value,
  onChange,
  onTextEditorInit,
  onTranslationEditorInit,
  swapTextTranslation,
}) => {
  const { t } = useTranslation();
  const pseudoType = value.pseudoType;

  return (
    <>
      <Field.Group>
        <Field.Label required>{t('title')}</Field.Label>
        <TextInput
          data-cy="post-composer_youtube-title"
          value={value.attachments?.title || ''}
          maxLength={PostsConfig.YOUTUBE_FORM.TITLE_MAX_LENGTH}
          onChange={(e) =>
            onChange((prev) => ({
              ...prev,
              attachments: {
                ...prev.attachments,
                title: e.target.value,
              },
            }))
          }
        />
        <Field.Caption>
          {t('maxCharsInfo', {
            maxChars: PostsConfig.YOUTUBE_FORM.TITLE_MAX_LENGTH,
          })}
        </Field.Caption>
      </Field.Group>
      <PostComposerContentText
        labels={{
          text: t('description'),
        }}
        value={value}
        onChange={onChange}
        onTextEditorInit={onTextEditorInit}
        onTranslationEditorInit={onTranslationEditorInit}
        swapTextTranslation={swapTextTranslation}
      />
      <div className="tw-flex tw-items-center tw-gap-2">
        <Field.Group className="tw-flex-1">
          <Field.Label required>{t('category')}</Field.Label>
          <Select
            inputId="post-composer_youtube-category"
            options={YoutubeFields.CATEGORIES}
            value={YoutubeFields.CATEGORIES.find(
              (category) => category.value === value.attachments.category,
            )}
            onChange={(selected) => {
              if (selected?.value) {
                onChange((prev) => ({
                  ...prev,
                  attachments: {
                    ...prev.attachments,
                    category: selected.value,
                  },
                }));
              }
            }}
          />
        </Field.Group>
        <Field.Group className="tw-flex-1">
          <Field.Label required>{t('visibility')}</Field.Label>
          <Select
            inputId="post-composer_youtube-visibility"
            options={YoutubeFields.VISIBILITY}
            isSearchable={false}
            value={YoutubeFields.VISIBILITY.find(
              (visibility) => visibility.value === value.attachments.visibility,
            )}
            onChange={(selected) => {
              if (selected?.value) {
                onChange((prev) => ({
                  ...prev,
                  attachments: {
                    ...prev.attachments,
                    visibility: selected.value,
                  },
                }));
              }
            }}
          />
        </Field.Group>
      </div>
      <Field.Group>
        <Field.Label required>{t('license')}</Field.Label>
        <Select
          inputId="post-composer_youtube-license"
          options={YoutubeFields.LICENSES}
          isSearchable={false}
          value={YoutubeFields.LICENSES.find(
            (license) => license.value === value.attachments.license,
          )}
          onChange={(selected) => {
            if (selected?.value) {
              onChange((prev) => ({
                ...prev,
                attachments: {
                  ...prev.attachments,
                  license: selected.value,
                },
              }));
            }
          }}
        />
      </Field.Group>
      <div className="tw-flex tw-items-center tw-gap-2">
        <Field.Group className="tw-flex tw-flex-1 !tw-flex-row tw-items-center">
          <Checkbox
            data-cy="post-composer_youtube-notify-subscribers"
            checked={value.attachments.notifySubscribers}
            value={value.attachments.notifySubscribers ? 'true' : 'false'}
            onChange={(e) =>
              onChange((prev) => ({
                ...prev,
                attachments: {
                  ...prev.attachments,
                  notifySubscribers: e.target.checked,
                },
              }))
            }
          />
          <Field.Label required>{t('notifySubscribers')}</Field.Label>
        </Field.Group>
        <Field.Group className="tw-flex tw-flex-1 !tw-flex-row tw-items-center">
          <Checkbox
            data-cy="post-composer_youtube-allow-embedding"
            checked={value.attachments.allowEmbedding}
            value={value.attachments.allowEmbedding ? 'true' : 'false'}
            onChange={(e) =>
              onChange((prev) => ({
                ...prev,
                attachments: {
                  ...prev.attachments,
                  allowEmbedding: e.target.checked,
                },
              }))
            }
          />
          <Field.Label required>{t('allowEmbedding')}</Field.Label>
        </Field.Group>
        <Field.Group className="tw-flex tw-flex-1 !tw-flex-row tw-items-center">
          <Checkbox
            data-cy="post-composer_youtube-allow-for-kids"
            checked={value.attachments.allowForKids}
            value={value.attachments.allowForKids ? 'true' : 'false'}
            onChange={(e) =>
              onChange((prev) => ({
                ...prev,
                attachments: {
                  ...prev.attachments,
                  allowForKids: e.target.checked,
                },
              }))
            }
          />
          <Field.Label required>{t('madeForKids')}</Field.Label>
        </Field.Group>
      </div>
      {pseudoType && (
        <>
          {[PseudoPostType.Video].includes(pseudoType) && (
            <PostComposerContentMediaAttachments
              value={value}
              onChange={onChange}
              allowedFiles={['video']}
              maxCount={PostsConfig.YOUTUBE_FORM.MAXIMUM_FILES_TO_ACCEPT}
            />
          )}
        </>
      )}
    </>
  );
};

export default PostComposerContentYoutube;
